import { isObject } from 'underscore';
import { createBlobWithOData, createBlobWithODataUsingAccountId } from './create-blob-with-odata';
import {
  AjaxOptions, AzureUploader, Logger, ODataService, BlobResponse, ODataUploaderParams,
  CreateBlobWithODataOptions,
  IODataAzureUploader,
} from '../types';

/**
 * Creates Azure Storage blobs via an odata request and uploads data to them.
 */
export class ODataAzureUploader implements IODataAzureUploader {
  ajaxOptions: AjaxOptions;

  azureUploader: AzureUploader;

  logger: Logger;

  odata: ODataService;

  /**
   * Creates a new instance of ODataAzureUploader.
   * @param {Object} odata - Instance of odata shared component, to request create of new blobs.
   * @param {Object} [ajaxOptions] - Additional options to send when making the odata request.
   * @param {AzureUploader} azureUploader - AzureUploader instance to use to upload blob data.
   * @param {Object} logger - Activity logger to write trace and error statements.
   */
  constructor({
    odata,
    ajaxOptions,
    azureUploader,
    logger,
  }: ODataUploaderParams) {
    this.odata = odata;
    this.ajaxOptions = ajaxOptions;
    this.azureUploader = azureUploader;
    this.logger = logger;

    if (!isObject(this.odata)) {
      throw new Error('Invalid odata object.');
    }

    if (!isObject(this.azureUploader)) {
      throw new Error('Invalid azureUploader object');
    }
  }

  /**
   * Create a new blob via odata call and write a payload to it.
   * @param {Object} options - Blob information and payload to be written.
   * @param {number} options.bulkFileType - Blob file type used in odata request.
   * @param {Payload} options.payload - Payload to upload, with content type and file extension.
   * @returns {Promise} - The async result of the blob creation and upload operations.
   */
  createBlobWithODataAndUpload({
    bulkFileType,
    payload,
    useAccountId = false,
  }: CreateBlobWithODataOptions) {
    let finalBlob: BlobResponse | null = null;

    return this.createBlobWithOData({
      fileExtension: payload!.getFileExtension(),
      bulkFileType,
      useAccountId,
    } as CreateBlobWithODataOptions)
      .then((blob: BlobResponse) => {
        finalBlob = blob;
        return this.azureUploader.uploadPayloadToBlob({ blob, payload: payload! });
      })
      .then(() => finalBlob);
  }

  /**
   * Create a new blob via odata call.
   * @param {Object} options - Blob information.
   * @param {string} options.fileExtension - Blob file extension.
   * @param {number} options.bulkFileType - Blob file type.
   * @returns {Promise} - The async result of the blob creation operation.
   */
  createBlobWithOData({
    fileExtension,
    bulkFileType,
    useAccountId = false,
  }: CreateBlobWithODataOptions) {
    if (useAccountId) {
      return createBlobWithODataUsingAccountId({
        fileExtension: fileExtension!,
        bulkFileType,
        odata: this.odata,
        ajaxOptions: this.ajaxOptions,
      }).then((blob: BlobResponse) => {
        this.logger.trace(
          `Successfully got SAS from odata - SasUrl: ${blob.SasUrl} blobName: ${blob.BlobName}`,
          'ODataAzureUploader/createBlobWithOdataAndUpload',
        );
        return blob;
      });
    }
    return createBlobWithOData({
      fileExtension: fileExtension!,
      bulkFileType,
      odata: this.odata,
      ajaxOptions: this.ajaxOptions,
    }).then((blob: BlobResponse) => {
      this.logger.trace(
        `Successfully got SAS from odata - SasUrl: ${blob.SasUrl} blobName: ${blob.BlobName}`,
        'ODataAzureUploader/createBlobWithOdataAndUpload',
      );
      return blob;
    });
  }
}
